import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// ── SEPARATE AXIOS INSTANCE FOR REVIEWER ────────────────────────────────────
// CRITICAL: Reviewer uses its OWN axios instance with its OWN token
// This prevents the reviewer token overwriting the filmmaker token
// which was causing the "film not showing" bug
const ReviewerAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
});

ReviewerAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem('sf_reviewer_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

ReviewerAPI.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('sf_reviewer_token');
      localStorage.removeItem('sf_reviewer');
      window.location.href = '/reviewer';
    }
    return Promise.reject(err);
  }
);

export { ReviewerAPI };

// ── CONTEXT ──────────────────────────────────────────────────────────────────
const ReviewerAuthContext = createContext(null);

export const ReviewerAuthProvider = ({ children }) => {
  const [reviewer, setReviewer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('sf_reviewer_token');
    const saved = localStorage.getItem('sf_reviewer');
    if (token && saved) {
      setReviewer(JSON.parse(saved));
      ReviewerAPI.get('/api/reviewer/auth/me')
        .then(res => setReviewer(res.data.reviewer))
        .catch(() => logout())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const loginWithGoogle = async (credential) => {
    const res = await ReviewerAPI.post('/api/reviewer/auth/google', { credential });
    const { token, reviewer } = res.data;
    localStorage.setItem('sf_reviewer_token', token);
    localStorage.setItem('sf_reviewer', JSON.stringify(reviewer));
    setReviewer(reviewer);
    return reviewer;
  };

  const loginWithPassword = async (email, password) => {
    const res = await ReviewerAPI.post('/api/reviewer/auth/login', { email, password });
    const { token, reviewer } = res.data;
    localStorage.setItem('sf_reviewer_token', token);
    localStorage.setItem('sf_reviewer', JSON.stringify(reviewer));
    setReviewer(reviewer);
    return reviewer;
  };

  const logout = () => {
    localStorage.removeItem('sf_reviewer_token');
    localStorage.removeItem('sf_reviewer');
    setReviewer(null);
  };

  const acceptNda = async () => {
    const res = await ReviewerAPI.post('/api/reviewer/nda/accept');
    const updated = { ...reviewer, ...res.data.reviewer };
    localStorage.setItem('sf_reviewer', JSON.stringify(updated));
    setReviewer(updated);
    return updated;
  };

  return (
    <ReviewerAuthContext.Provider value={{ reviewer, loading, loginWithGoogle, loginWithPassword, logout, acceptNda }}>
      {children}
    </ReviewerAuthContext.Provider>
  );
};

export const useReviewerAuth = () => useContext(ReviewerAuthContext);
